import { useState, useCallback } from "react";
import { useUrlSyncedState } from "@/features/assignment/hooks/useUrlSyncedState";
import { Order } from "@/shared/api/lod-api";
import { SortDirection } from "../lib/types";
import { SORT_DIRECTIONS } from "../lib/consts";
import { SOURCE_OPTIONS } from "@/shared/consts";
import { isValidDateString } from "../lib/helpers";
import { useUrlQueries } from "@/shared/hooks/useUrlQueries";
import {
  convertFromUtcPreserveTime,
  convertToUtcPreserveTime,
} from "@/shared/helpers/datetime";
import { useEffectOnce } from "react-use";
import { AnnotationFilterParams } from "@/shared/types/filters";
import { FILTER_KEYS } from "@/features/assignment/hooks/useAssignmentFilters";

export const ANNOTATION_FILTER_KEYS = {
  SEARCH: "search",
  ORDER_SOURCE: "order_source",
  ASSIGNEE: "annotator_id",
  FROM_DATETIME: "from_datetime",
  TO_DATETIME: "to_datetime",
  ORDERING: "ordering",
  SHOW_FILTERS: "show_filters",
  STAGE_JOURNEY_ANNOTATION: "stage_journey_annotation",
  STAGE_TRANSACTION_ANNOTATION: "stage_transaction_annotation",
  STAGE_TRACK_PERSISTENCE_ANNOTATION: "stage_track_persistence_annotation",
} as const;

export type AnnotationFilterKey =
  (typeof ANNOTATION_FILTER_KEYS)[keyof typeof ANNOTATION_FILTER_KEYS];

export const useAnnotationFilters = () => {
  const { getParam, updateParams } = useUrlSyncedState();
  const { removeQueries } = useUrlQueries();

  const [orderId, setOrderId] = useState(
    getParam(ANNOTATION_FILTER_KEYS.SEARCH) || ""
  );
  const [source, setSource] = useState<Order["order_source"]>(
    (getParam(ANNOTATION_FILTER_KEYS.ORDER_SOURCE) as Order["order_source"]) ||
      "all"
  );
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [assignee, setAssignee] = useState<string>(
    getParam(ANNOTATION_FILTER_KEYS.ASSIGNEE) || "all"
  );
  const [stageJourneyAnnotation, setStageJourneyAnnotation] = useState<string>(
    getParam(ANNOTATION_FILTER_KEYS.STAGE_JOURNEY_ANNOTATION) || "all"
  );
  const [stageTransactionAnnotation, setStageTransactionAnnotation] =
    useState<string>(
      getParam(ANNOTATION_FILTER_KEYS.STAGE_TRANSACTION_ANNOTATION) || "all"
    );
  const [stageTrackPersistenceAnnotation, setStageTrackPersistenceAnnotation] =
    useState<string>(
      getParam(ANNOTATION_FILTER_KEYS.STAGE_TRACK_PERSISTENCE_ANNOTATION) ||
        "all"
    );

  const getInitialSortDirection = () => {
    const ordering = getParam(ANNOTATION_FILTER_KEYS.ORDERING);
    if (ordering === "time") return SORT_DIRECTIONS.ASC;
    if (ordering === "-time") return SORT_DIRECTIONS.DESC;
    return SORT_DIRECTIONS.DESC;
  };

  const [sortDirection, setSortDirection] = useState<SortDirection>(
    getInitialSortDirection()
  );
  const [showFilters, setShowFilters] = useState<boolean>(
    getParam(ANNOTATION_FILTER_KEYS.SHOW_FILTERS) === "true"
  );
  const [isFiltersApplied, setIsFiltersApplied] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [activeFilters, setActiveFilters] = useState<AnnotationFilterParams>(
    {}
  );

  const initializeFiltersFromUrl = useCallback(() => {
    const paramValues = {
      search: getParam(ANNOTATION_FILTER_KEYS.SEARCH),
      orderSource: getParam(ANNOTATION_FILTER_KEYS.ORDER_SOURCE),
      fromDatetime: getParam(ANNOTATION_FILTER_KEYS.FROM_DATETIME),
      toDatetime: getParam(ANNOTATION_FILTER_KEYS.TO_DATETIME),
      ordering: getParam(ANNOTATION_FILTER_KEYS.ORDERING),
      showFilters: getParam(ANNOTATION_FILTER_KEYS.SHOW_FILTERS),
      stageJourneyAnnotation: getParam(
        ANNOTATION_FILTER_KEYS.STAGE_JOURNEY_ANNOTATION
      ),
      stageTransactionAnnotation: getParam(
        ANNOTATION_FILTER_KEYS.STAGE_TRANSACTION_ANNOTATION
      ),
      stageTrackPersistenceAnnotation: getParam(
        ANNOTATION_FILTER_KEYS.STAGE_TRACK_PERSISTENCE_ANNOTATION
      ),
      assignee: getParam(ANNOTATION_FILTER_KEYS.ASSIGNEE),
    };

    const initialFilters: AnnotationFilterParams = {};

    if (paramValues.search) {
      initialFilters[ANNOTATION_FILTER_KEYS.SEARCH] = paramValues.search;
    }

    if (paramValues.orderSource && paramValues.orderSource !== "all") {
      initialFilters[ANNOTATION_FILTER_KEYS.ORDER_SOURCE] =
        paramValues.orderSource;
    }

    if (
      paramValues.fromDatetime &&
      paramValues.toDatetime &&
      isValidDateString(paramValues.fromDatetime) &&
      isValidDateString(paramValues.toDatetime)
    ) {
      initialFilters[ANNOTATION_FILTER_KEYS.FROM_DATETIME] =
        paramValues.fromDatetime;
      initialFilters[ANNOTATION_FILTER_KEYS.TO_DATETIME] =
        paramValues.toDatetime;

      setFromDate(parseDateParam(paramValues.fromDatetime));
      setToDate(parseDateParam(paramValues.toDatetime));
    } else {
      removeQueries(["from_datetime", "to_datetime"]);
    }

    if (paramValues.ordering) {
      initialFilters[ANNOTATION_FILTER_KEYS.ORDERING] = paramValues.ordering;
    } else {
      initialFilters[ANNOTATION_FILTER_KEYS.ORDERING] = "-time";
    }

    if (paramValues.showFilters) {
      initialFilters[ANNOTATION_FILTER_KEYS.SHOW_FILTERS] =
        paramValues.showFilters;
    } else {
      initialFilters[ANNOTATION_FILTER_KEYS.SHOW_FILTERS] = "false";
    }

    if (paramValues.stageJourneyAnnotation) {
      initialFilters[ANNOTATION_FILTER_KEYS.STAGE_JOURNEY_ANNOTATION] =
        paramValues.stageJourneyAnnotation;
    }

    if (paramValues.stageTransactionAnnotation) {
      initialFilters[ANNOTATION_FILTER_KEYS.STAGE_TRANSACTION_ANNOTATION] =
        paramValues.stageTransactionAnnotation;
    }

    if (paramValues.stageTrackPersistenceAnnotation) {
      initialFilters[
        ANNOTATION_FILTER_KEYS.STAGE_TRACK_PERSISTENCE_ANNOTATION
      ] = paramValues.stageTrackPersistenceAnnotation;
    }

    if (paramValues.assignee && paramValues.assignee !== "all") {
      initialFilters[ANNOTATION_FILTER_KEYS.ASSIGNEE] = paramValues.assignee;
    }

    setActiveFilters(initialFilters);
    const contentFilters = Object.entries(initialFilters).filter(
      ([key]) => key !== ANNOTATION_FILTER_KEYS.SHOW_FILTERS
    );
    setIsFiltersApplied(contentFilters.length > 0);
    setIsInitialized(true);
  }, [getParam, updateParams]);

  useEffectOnce(() => {
    initializeFiltersFromUrl();
  });

  const onOrderIdChange = useCallback((value: string) => {
    setOrderId(value);
    setIsFiltersApplied(false);
  }, []);

  const onSourceChange = useCallback((value: string) => {
    setSource(value as Order["order_source"]);
    setIsFiltersApplied(false);
  }, []);

  const onFromDateChange = useCallback((date: Date | null) => {
    setFromDate(date);
    setIsFiltersApplied(false);
  }, []);

  const onToDateChange = useCallback((date: Date | null) => {
    setToDate(date);
    setIsFiltersApplied(false);
  }, []);

  const onAssigneeChange = useCallback((value: string) => {
    setAssignee(value);
    setIsFiltersApplied(false);
  }, []);

  const onSortDirectionChange = useCallback((value: SortDirection) => {
    setSortDirection(value);
    setIsFiltersApplied(false);
  }, []);

  const onStageJourneyAnnotationChange = useCallback((value: string) => {
    setStageJourneyAnnotation(value);
    setIsFiltersApplied(false);
  }, []);

  const onStageTransactionAnnotationChange = useCallback((value: string) => {
    setStageTransactionAnnotation(value);
    setIsFiltersApplied(false);
  }, []);

  const onStageTrackPersistenceAnnotationChange = useCallback(
    (value: string) => {
      setStageTrackPersistenceAnnotation(value);
      setIsFiltersApplied(false);
    },
    []
  );

  const parseDateParam = useCallback((dateString: string | null) => {
    return dateString ? convertFromUtcPreserveTime(dateString) : null;
  }, []);

  const toggleFilters = useCallback(() => {
    const newValue = !showFilters;
    setShowFilters(newValue);
    updateParams({
      [ANNOTATION_FILTER_KEYS.SHOW_FILTERS]: newValue.toString(),
    });
  }, [showFilters, updateParams]);

  const applyFilters = useCallback(() => {
    const orderingValue =
      sortDirection === SORT_DIRECTIONS.DESC ? "-time" : "time";
    const showFiltersReverseValue = showFilters ? "false" : "true";

    const urlParams: AnnotationFilterParams = {
      [ANNOTATION_FILTER_KEYS.SEARCH]: orderId || undefined,
      [ANNOTATION_FILTER_KEYS.ORDER_SOURCE]:
        source !== "all" ? source : undefined,
      [ANNOTATION_FILTER_KEYS.FROM_DATETIME]: fromDate
        ? convertToUtcPreserveTime(fromDate)
        : undefined,
      [ANNOTATION_FILTER_KEYS.TO_DATETIME]: toDate
        ? convertToUtcPreserveTime(toDate)
        : undefined,
      [ANNOTATION_FILTER_KEYS.ASSIGNEE]:
        assignee !== "all" ? assignee : undefined,
      [ANNOTATION_FILTER_KEYS.ORDERING]: orderingValue,
      [ANNOTATION_FILTER_KEYS.SHOW_FILTERS]: showFiltersReverseValue,
      [ANNOTATION_FILTER_KEYS.STAGE_JOURNEY_ANNOTATION]:
        stageJourneyAnnotation !== "all" ? stageJourneyAnnotation : undefined,
      [ANNOTATION_FILTER_KEYS.STAGE_TRANSACTION_ANNOTATION]:
        stageTransactionAnnotation !== "all"
          ? stageTransactionAnnotation
          : undefined,
      [ANNOTATION_FILTER_KEYS.STAGE_TRACK_PERSISTENCE_ANNOTATION]:
        stageTrackPersistenceAnnotation !== "all"
          ? stageTrackPersistenceAnnotation
          : undefined,
    };

    const activeFiltersData: AnnotationFilterParams = {
      [ANNOTATION_FILTER_KEYS.SEARCH]: orderId || undefined,
      [ANNOTATION_FILTER_KEYS.ORDER_SOURCE]:
        source !== "all" ? source : undefined,
      [ANNOTATION_FILTER_KEYS.ASSIGNEE]:
        assignee !== "all" ? assignee : undefined,
      [ANNOTATION_FILTER_KEYS.ORDERING]: orderingValue,
      [FILTER_KEYS.FROM_DATETIME]: fromDate
        ? convertToUtcPreserveTime(fromDate)
        : undefined,
      [FILTER_KEYS.TO_DATETIME]: toDate
        ? convertToUtcPreserveTime(toDate)
        : undefined,
      [ANNOTATION_FILTER_KEYS.SHOW_FILTERS]: showFiltersReverseValue,
      [ANNOTATION_FILTER_KEYS.STAGE_JOURNEY_ANNOTATION]:
        stageJourneyAnnotation !== "all" ? stageJourneyAnnotation : undefined,
      [ANNOTATION_FILTER_KEYS.STAGE_TRANSACTION_ANNOTATION]:
        stageTransactionAnnotation !== "all"
          ? stageTransactionAnnotation
          : undefined,
      [ANNOTATION_FILTER_KEYS.STAGE_TRACK_PERSISTENCE_ANNOTATION]:
        stageTrackPersistenceAnnotation !== "all"
          ? stageTrackPersistenceAnnotation
          : undefined,
    };

    setActiveFilters(activeFiltersData);
    setIsFiltersApplied(true);
    updateParams(urlParams);
    setShowFilters(!showFilters);

    return activeFiltersData;
  }, [
    orderId,
    source,
    fromDate,
    toDate,
    assignee,
    sortDirection,
    showFilters,
    stageJourneyAnnotation,
    stageTransactionAnnotation,
    stageTrackPersistenceAnnotation,
    updateParams,
  ]);

  const clearFilters = useCallback(() => {
    setOrderId("");
    setSource("all");
    setFromDate(null);
    setToDate(null);
    setSortDirection(SORT_DIRECTIONS.DESC);
    setAssignee("all");
    setStageJourneyAnnotation("all");
    setStageTransactionAnnotation("all");
    setStageTrackPersistenceAnnotation("all");

    const newFilters: AnnotationFilterParams = {
      [ANNOTATION_FILTER_KEYS.ORDERING]: "-time",
      [ANNOTATION_FILTER_KEYS.SHOW_FILTERS]: showFilters.toString(),
    };

    setActiveFilters(newFilters);
    setIsFiltersApplied(false);

    const paramsToClear = [
      ANNOTATION_FILTER_KEYS.SEARCH,
      ANNOTATION_FILTER_KEYS.ORDER_SOURCE,
      ANNOTATION_FILTER_KEYS.FROM_DATETIME,
      ANNOTATION_FILTER_KEYS.TO_DATETIME,
      ANNOTATION_FILTER_KEYS.ORDERING,
      ANNOTATION_FILTER_KEYS.ASSIGNEE,
      ANNOTATION_FILTER_KEYS.STAGE_JOURNEY_ANNOTATION,
      ANNOTATION_FILTER_KEYS.STAGE_TRANSACTION_ANNOTATION,
      ANNOTATION_FILTER_KEYS.STAGE_TRACK_PERSISTENCE_ANNOTATION,
    ];

    const paramsToUpdate = {
      ...paramsToClear.reduce(
        (acc, key) => ({ ...acc, [key]: null }),
        {} as Record<string, null | string>
      ),
      [ANNOTATION_FILTER_KEYS.ORDERING]:
        newFilters[ANNOTATION_FILTER_KEYS.ORDERING],
      [ANNOTATION_FILTER_KEYS.SHOW_FILTERS]:
        newFilters[ANNOTATION_FILTER_KEYS.SHOW_FILTERS],
    };

    updateParams(paramsToUpdate);
    return newFilters;
  }, [showFilters, updateParams]);

  const updateOrdering = useCallback((ordering: string) => {
    if (ordering === "time") {
      setSortDirection(SORT_DIRECTIONS.ASC);
    } else if (ordering === "-time") {
      setSortDirection(SORT_DIRECTIONS.DESC);
    } else {
      setSortDirection(SORT_DIRECTIONS.DESC);
    }
    setIsFiltersApplied(false);
  }, []);

  const clearOrdering = useCallback(() => {
    setActiveFilters((prevFilters) => {
      const { [ANNOTATION_FILTER_KEYS.ORDERING]: _, ...rest } = prevFilters;
      return rest;
    });
    updateParams({ [ANNOTATION_FILTER_KEYS.ORDERING]: null });
  }, [updateParams]);

  return {
    filterState: {
      orderId,
      source,
      toDate,
      fromDate,
      assignee,
      sortDirection,
      showFilters,
      isFiltersApplied,
      isInitialized,
      stageJourneyAnnotation,
      stageTransactionAnnotation,
      stageTrackPersistenceAnnotation,
    },
    filterHandlers: {
      onOrderIdChange,
      onSourceChange,
      onFromDateChange,
      onToDateChange,
      onAssigneeChange,
      onSortDirectionChange,
      toggleFilters,
      applyFilters,
      clearFilters,
      updateOrdering,
      clearOrdering,
      onStageJourneyAnnotationChange,
      onStageTransactionAnnotationChange,
      onStageTrackPersistenceAnnotationChange,
    },
    activeFilters,
    sourceList: SOURCE_OPTIONS,
  };
};
